import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { BaseAsyncValue } from '../../../../../shared/models/async-value';
import {
  ExperiencesService,
  TemperatureFeatures,
  ValueAddedCategoryCode,
} from '../../../../../shared/services/experiences.service';
import { debounceTime, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { getRmsRecipeSpecifications } from '../../selectors';
import { SpecFieldNames } from '../../../specification/constants/specification-field-names.enum';
import { getTemperatureVaps } from '../../selectors/added-value.selectors';
import { _safe } from '../../../../../shared/utils/safe-get.method';
import { Validators } from '@angular/forms';
import { FieldLabelConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-label-config.interface';
import { SpecificationFormValues } from '../../../specification/models/specification-form-values';
import { filterRetardantValueAdded, getRefrigerantValueAdded } from '@ordersnproducts/utils/temperature.util';
import { getFirstCalendar } from '@ordersnproducts/modules/order-builder/reducers';

export namespace TemperatureFeatureEnabler {
  export class ValidateTemperatureNitroExperience extends BaseAsyncValue<boolean> {
    private experiencesService: ExperiencesService;
    protected feature = TemperatureFeatures.NITRO;
    getValue(): Observable<boolean> {
      this.experiencesService = this.injector.get(ExperiencesService);
      const temperatureEnable$ = this.experiencesService.isTemperatureFeatureEnable$(this.feature).pipe(
        take(1),
        map((isEnabled) => !isEnabled),
      );
      return temperatureEnable$;
    }
  }

  export class ValidateIceFieldSummary extends BaseAsyncValue<boolean> {
    private experiencesService: ExperiencesService;
    protected feature = TemperatureFeatures.ICE;
    getValue(): Observable<boolean> {
      this.experiencesService = this.injector.get(ExperiencesService);

      return combineLatest([
        this.experiencesService.isTemperatureFeatureEnable$(this.feature).pipe(
          take(1),
          map((isEnabled) => !isEnabled),
        ),
        this.store.select(getTemperatureVaps),
      ]).pipe(
        map(([disabled, valuesAdded]) => {
          if (disabled) {
            return disabled;
          }
          let mTmpVap = _safe(
            () => valuesAdded[this.mapItem.id].find((x) => x.valueAddedCategoryCode === ValueAddedCategoryCode.ICE),
            undefined,
          );
          if (!!mTmpVap) {
            return !mTmpVap;
          }
          mTmpVap = _safe(
            () => valuesAdded[0].find((x) => x.valueAddedCategoryCode === ValueAddedCategoryCode.ICE),
            undefined,
          );
          return !mTmpVap;
        }),
      );
    }
  }

  export class ValidateTemperatureNitroExperienceSummary extends BaseAsyncValue<boolean> {
    private experiencesService: ExperiencesService;
    protected feature = TemperatureFeatures.NITRO;
    getValue(): Observable<boolean> {
      this.experiencesService = this.injector.get(ExperiencesService);
      const temperatureEnable$ = this.experiencesService.isTemperatureFeatureEnable$(this.feature).pipe(
        take(1),
        map((isEnabled) => !isEnabled),
        map((disabled) => {
          const index = (this.config as FieldLabelConfig).index;
          const recipeItem = (this.mapItem as SpecificationFormValues).values;
          if (disabled) {
            return true;
          }
          const values = index ? recipeItem[index] : recipeItem;
          if (values[SpecFieldNames.TEMPERATURE_UNDER_DISABLED] === undefined) {
            return true;
          }
          return !!values[SpecFieldNames.TEMPERATURE_UNDER_DISABLED];
        }),
      );
      return temperatureEnable$;
    }
  }

  /**
   * Class ValidateSummersetSummary
   *
   * This class extends BaseAsyncValue<boolean> and is responsible for validating
   * the Summerset summary based on the temperature feature enablement.
   */
  export class ValidateSummersetSummary extends BaseAsyncValue<boolean> {
    private experiencesService: ExperiencesService;
    protected feature = TemperatureFeatures.NITRO;
    /**
     * This method checks if the temperature feature is enabled and then performs
     * additional checks on the configuration and mapped item values to determine
     * if summer set should be disabled.
     *
     * @returns {Observable<boolean>} An observable emitting the validation result.
     */
    getValue(): Observable<boolean> {
      this.experiencesService = this.injector.get(ExperiencesService);
      const temperatureEnable$ = this.experiencesService.isTemperatureFeatureEnable$(this.feature).pipe(
        take(1),
        map((isEnabled) => !isEnabled),
        map((disabled) => {
          const index = (this.config as FieldLabelConfig).index;
          const recipeItem = (this.mapItem as SpecificationFormValues).values;
          if (disabled) {
            return true;
          }
          const values = index ? recipeItem[index] : recipeItem;
          if (values[SpecFieldNames.SUMMER_SET_DISABLED]) {
            return true;
          }
          if (!values[SpecFieldNames.SUMMER_SET]) {
            return false;
          }
          return !!values[SpecFieldNames.SUMMER_SET_DISABLED];
        }),
      );
      return temperatureEnable$;
    }
  }

  export class ValidateTemperatureNitroExperienceConfirmation extends BaseAsyncValue<boolean> {
    private experiencesService: ExperiencesService;
    protected feature = TemperatureFeatures.NITRO;
    getValue(): Observable<boolean> {
      this.experiencesService = this.injector.get(ExperiencesService);
      const specHidden$ = this.store.select(getRmsRecipeSpecifications).pipe(
        take(1),
        map((forms) => forms[0].values),
        switchMap((values) => of(values[SpecFieldNames.TEMPERATURE_UNDER_DISABLED])),
        map((hidden) => !!hidden),
      );
      const temperatureEnable$ = this.experiencesService.isTemperatureFeatureEnable$(this.feature).pipe(
        take(1),
        map((isEnabled) => !isEnabled),
        map((disabled) => {
          if (disabled) {
            return true;
          }
          const item = this.mapItem;
          if (!!item.product && item.product.temperature.temperatureCode) {
            return false;
          }
          return true;
        }),
      );
      return specHidden$.pipe(
        switchMap((hidden) => {
          if (hidden) {
            return of(true);
          }
          return temperatureEnable$;
        }),
      );
    }
  }

  /**
   * Class ValidateSummersetNitroExperienceConfirmation
   *
   * This class is responsible for validating the Summerset in the
   * confirmation screen based on the temperature feature enablement.
   */
  export class ValidateSummersetNitroExperienceConfirmation extends BaseAsyncValue<boolean> {
    private experiencesService: ExperiencesService;
    protected feature = TemperatureFeatures.NITRO;

    /**
     * Retrieves the validation value as an Observable<boolean>.
     *
     * This method checks if the temperature feature is enabled and performs additional
     * checks on the RMS recipe specifications to determine it summer ser should be hidden or not.
     * It is used it the confirmation screen.
     *
     * @returns {Observable<boolean>} An observable emitting the validation result.
     */
    getValue(): Observable<boolean> {
      this.experiencesService = this.injector.get(ExperiencesService);
      const specHidden$ = this.store.select(getRmsRecipeSpecifications).pipe(
        take(1),
        map((forms) => forms[0].values),
        switchMap((values) => of(values[SpecFieldNames.SUMMER_SET_DISABLED])),
        map((hidden) => !!hidden),
      );
      const temperatureEnable$ = this.experiencesService.isTemperatureFeatureEnable$(this.feature).pipe(
        take(1),
        map((isEnabled) => !isEnabled),
        map((disabled) => {
          if (disabled) {
            return true;
          }
          return false;
        }),
      );
      return specHidden$.pipe(
        switchMap((hidden) => {
          if (hidden) {
            return of(true);
          } else {
            return temperatureEnable$;
          }
        }),
      );
    }
  }

  export class ValidateTemperatureIceExperience extends ValidateTemperatureNitroExperience {
    protected feature = TemperatureFeatures.ICE;
  }

  export class TemperatureConditionValidation extends BaseAsyncValue<boolean> {
    protected feature = TemperatureFeatures.NITRO;
    getValue(): Observable<boolean> {
      return this.store.select(getRmsRecipeSpecifications).pipe(
        switchMap((recipes) => {
          const formBase = this.form;
          const formId = !!formBase ? Number(this.form.value.id) : null;
          const validData = !!recipes && recipes.length > 0 && formId !== null;
          const recipe = recipes.find((formValues) => formValues.id === formId);
          if (validData && !!recipe) {
            return of(recipe);
          }
          return EMPTY;
        }),
        map((rmsRecipeSpecifications) => {
          const temperatureUnderDisabled = this.form.get(SpecFieldNames.TEMPERATURE_UNDER_DISABLED);
          if (!!(temperatureUnderDisabled?.value ?? true)) {
            return true;
          }
          return rmsRecipeSpecifications.values[SpecFieldNames.TEMPERATURE_UNDER] !== 'NONE';
        }),
      );
    }
  }

  export class DisabledByContractVapHaveRefrigerant extends BaseAsyncValue<boolean> {
    private experiencesService: ExperiencesService;
    getValue(): Observable<boolean> {
      this.experiencesService = this.injector.get(ExperiencesService);
      const temperatureEnable$ = this.experiencesService.isTemperatureEnabled$().pipe(
        take(1),
        map((isEnabled) => isEnabled !== null),
      );
      const vaps$ = this.store.select(getTemperatureVaps);

      const disabled$ = combineLatest([temperatureEnable$, vaps$]).pipe(
        filter(([temperatureEnable, vaps]) => temperatureEnable === true),
        switchMap(([temperatureEnable, vaps]) => {
          const formBase = this.form;
          const formId = !!formBase ? Number(this.form.value.id) : null;
          if (formId === null) {
            return of(true);
          }
          const refrigerant = _safe(
            () => vaps[formId].find((x) => x.valueAddedCategoryCode === ValueAddedCategoryCode.ICE),
            undefined,
          );
          const disabled = refrigerant === undefined;
          if (!disabled) {
            this.form.get(SpecFieldNames.ICE_QUANTITY)?.enable();
          } else {
            this.form.get(SpecFieldNames.ICE_QUANTITY)?.disable();
          }
          return of(disabled);
        }),
      );
      return disabled$;
    }
  }

  export class DisableIfContractNotSelected extends BaseAsyncValue<boolean> {
    getValue(): Observable<boolean> {
      const getCalendarOrders$ = this.store.select(getFirstCalendar).pipe(
        filter((calendar) => !!calendar),
        take(1),
      );
      const Vaps = this.store.select(getTemperatureVaps);
      return combineLatest([Vaps, getCalendarOrders$]).pipe(
        debounceTime(100),
        switchMap(([vaps, calendar]) => {
          const formBase = this.form;
          const formId = !!formBase ? Number(this.form.value.id) : null;
          if (formId === null) {
            return of(true);
          }
          const refrigerant = _safe(() => getRefrigerantValueAdded(vaps[formId], calendar?.tempControlFlag), undefined);
          const retard = filterRetardantValueAdded(vaps[formId]).length > 0;
          if (!!refrigerant && !!retard) {
            this.form.get(SpecFieldNames.SUMMER_SET).enable();
            this.form.get(SpecFieldNames.SUMMER_SET_DISABLED).setValue(false);
            this.form.get(SpecFieldNames.TEMPERATURE_UNDER).enable();
            this.form.get(SpecFieldNames.TEMPERATURE_UNDER).setValidators([Validators.required]);
            this.form.get(SpecFieldNames.TEMPERATURE_UNDER).updateValueAndValidity();
            this.form.get(SpecFieldNames.TEMPERATURE_UNDER_DISABLED).setValue(false);
          }

          if (!retard) {
            this.form.get(SpecFieldNames.SUMMER_SET).disable();
            this.form.get(SpecFieldNames.SUMMER_SET).setValidators([]);
            this.form.get(SpecFieldNames.SUMMER_SET).updateValueAndValidity();
            this.form.get(SpecFieldNames.SUMMER_SET_DISABLED).setValue(true);
          }

          if (!refrigerant) {
            this.form.get(SpecFieldNames.TEMPERATURE_UNDER).disable();
            this.form.get(SpecFieldNames.TEMPERATURE_UNDER).setValidators([]);
            this.form.get(SpecFieldNames.TEMPERATURE_UNDER_DISABLED).setValue(true);
          }

          if (!refrigerant && retard) {
            this.form.get(SpecFieldNames.SUMMER_SET).enable();
            this.form.get(SpecFieldNames.SUMMER_SET_DISABLED).setValue(false);
          }

          return of(this.form.get(SpecFieldNames.TEMPERATURE_UNDER_DISABLED).value);
        }),
      );
    }
  }

  /**
   * Class AlignTempSummerValuesSummary
   *
   * This class is responsible for aligning the temperature and
   * summer set values summary based on the RMS recipe specifications.
   */
  export class AlignTempSummerValuesSummary extends BaseAsyncValue<string[]> {
    /**
     * Retrieves the alignment values as an Observable<string[]>.
     *
     * This method selects the RMS recipe specifications from the store and maps the
     * values to determine the appropriate column size for an empty space in the form
     * based on the temperature and summer set statuses. It is used for Summary screen.
     *
     * @returns {Observable<string[]>} An observable emitting the alignment values.
     */
    getValue(): Observable<string[]> {
      const experiencesService: ExperiencesService = this.injector.get(ExperiencesService);
      return this.store.select(getRmsRecipeSpecifications).pipe(
        takeUntil(this.ngDestroy),
        map((forms) => {
          if (experiencesService.isArkikUlEnabled()) {
            return ['col-md-12'];
          } else {
            const valuesForm = forms[0].values;
            const tempDis = valuesForm[SpecFieldNames.TEMPERATURE_UNDER_DISABLED];
            const sumDis = valuesForm[SpecFieldNames.SUMMER_SET_DISABLED];
            if (!tempDis && !sumDis) {
              return ['col-md-7'];
            }
            if (tempDis && !sumDis) {
              return ['col-md-9'];
            }
            return ['col-md-12'];
          }
        }),
      );
    }
  }

  /**
   * Class AlignTempSummerValuesOnConfirmation
   *
   * This class is responsible for aligning the temperature and summer
   * set values on confirmation based on the RMS recipe specifications.
   */
  export class AlignTempSummerValuesOnConfirmation extends BaseAsyncValue<string[]> {
    /**
     * Retrieves the alignment values as an Observable<string[]>.
     *
     * This method selects the RMS recipe specifications from the store and maps the
     * values to determine the appropriate column size based on the temperature and
     * summer set disabled statuses. It is used for confirmation screen.
     *
     * @returns {Observable<string[]>} An observable emitting the alignment values.
     */
    getValue(): Observable<string[]> {
      return this.store.select(getRmsRecipeSpecifications).pipe(
        takeUntil(this.ngDestroy),
        map((forms) => {
          const valuesForm = forms[0].values;
          const tempDis = valuesForm[SpecFieldNames.TEMPERATURE_UNDER_DISABLED];
          const sumDis = valuesForm[SpecFieldNames.SUMMER_SET_DISABLED];
          if (tempDis && !sumDis) {
            return ['col-md-2'];
          }
          return ['d-none'];
        }),
      );
    }
  }
}
